import { useEffect, useRef, useState } from "react";
import { MenuItem, Stack, TextField } from "@mui/material";
import {
  ENVIRONMENTS,
  Environment,
  GOV_PRIORITIES,
  GovItemDetail,
  GovPriority,
  SEVERITIES,
  Severity,
  createItem,
  governanceErrorMessage,
} from "../../../../utils/icGovernance";
import { ENVIRONMENT_LABEL, SEVERITY_LABEL, priorityLabel } from "../governanceStatus";
import { CreateDialogShell, optional } from "./dialogParts";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (item: GovItemDetail) => void;
};

// Support's ticket fields plus the Issue and Bug Log's own columns.
const NewIssueDialog = ({ open, onClose, onCreated }: Props) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<GovPriority>("MEDIUM");
  const [environment, setEnvironment] = useState<Environment | "">("");
  const [severity, setSeverity] = useState<Severity | "">("");
  const [steps, setSteps] = useState("");
  const [expected, setExpected] = useState("");
  const [actual, setActual] = useState("");
  const [impact, setImpact] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setPriority("MEDIUM");
      setEnvironment("");
      setSeverity("");
      setSteps("");
      setExpected("");
      setActual("");
      setImpact("");
      setError(null);
      setSaving(false);
      savingRef.current = false;
    }
  }, [open]);

  const canSubmit = title.trim().length > 0 && description.trim().length > 0;

  const submit = async () => {
    if (!canSubmit || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const item = await createItem({
        kind: "ISSUE",
        title: title.trim(),
        description: description.trim(),
        priority,
        environment: environment || undefined,
        severity: severity || undefined,
        stepsToReproduce: optional(steps),
        expectedResult: optional(expected),
        actualResult: optional(actual),
        businessImpact: optional(impact),
      });
      onCreated(item);
    } catch (err) {
      setError(governanceErrorMessage(err));
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <CreateDialogShell
      open={open}
      titleId="new-issue-title"
      title="New issue"
      saving={saving}
      error={error}
      canSubmit={canSubmit}
      submitLabel="Submit issue"
      onClose={onClose}
      onSubmit={submit}
    >
      <TextField
        label="Title"
        placeholder="Short summary of the problem"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
        autoFocus
        fullWidth
        disabled={saving}
      />
      <TextField
        label="Description"
        placeholder="What happened, where in Thrive, and what you expected instead"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        required
        multiline
        minRows={4}
        fullWidth
        disabled={saving}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          select
          label="Priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value as GovPriority)}
          fullWidth
          disabled={saving}
        >
          {GOV_PRIORITIES.map((p) => (
            <MenuItem key={p} value={p}>
              {priorityLabel(p)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Environment"
          value={environment}
          onChange={(e) => setEnvironment(e.target.value as Environment | "")}
          fullWidth
          disabled={saving}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {ENVIRONMENTS.map((env) => (
            <MenuItem key={env} value={env}>
              {ENVIRONMENT_LABEL[env] ?? env}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Severity"
          value={severity}
          onChange={(e) => setSeverity(e.target.value as Severity | "")}
          fullWidth
          disabled={saving}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {SEVERITIES.map((sv) => (
            <MenuItem key={sv} value={sv}>
              {SEVERITY_LABEL[sv] ?? sv}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <TextField
        label="Steps to reproduce"
        value={steps}
        onChange={(e) => setSteps(e.target.value)}
        multiline
        minRows={3}
        fullWidth
        disabled={saving}
      />
      <TextField
        label="Expected result"
        value={expected}
        onChange={(e) => setExpected(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        disabled={saving}
      />
      <TextField
        label="Actual result"
        value={actual}
        onChange={(e) => setActual(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        disabled={saving}
      />
      <TextField
        label="Business impact"
        value={impact}
        onChange={(e) => setImpact(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        disabled={saving}
      />
    </CreateDialogShell>
  );
};

export default NewIssueDialog;
